import { useRef, useState, useCallback, useEffect } from 'react'
import { useSelectedCompany } from '../auth/CompanyContext'
import { useYear } from '../auth/YearContext'
import { parseSie } from '../lib/sieParser'
import {
  applyMonthsCloud,
  uploadReceiptCloud,
  checkDuplicates,
  scanAllDuplicates,
  saveDupScanTimestamp,
  subscribeYear,
  subscribeReceipts,
  aiAnalyzeReceipt,
  deleteReceiptCloud,
  computeQuality,
  updateReceiptFields,
  recordSync,
  type CloudReceipt,
  type ReceiptQuality,
  type DuplicateMatch,
  type DupScanGroup,
  type CloudYearDoc,
} from '../lib/cloudStore'
import { parseFilename, receiptListTitle, type ReceiptCategory } from '../lib/receipts'
import { ImportOverview } from '../components/ImportOverview'
import { SyncStatusCard } from '../components/SyncStatusCard'
import { RunNowButton } from '../components/RunNowButton'
import { DuplicateComparePopup } from '../components/DuplicateComparePopup'
import { JsonImportDialog } from '../components/JsonImportDialog'
import { MomsBalanceCard } from '../components/MomsBalanceCard'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

const CATEGORIES: { id: ReceiptCategory; label: string; description: string; accent: string }[] = [
  {
    id: 'kundfakturor',
    label: 'Kundfakturor (Fakturor in)',
    description: 'Int\u00e4kter \u2014 fakturor du skickat och f\u00e5tt betalt f\u00f6r',
    accent: '#10b981',
  },
  {
    id: 'leverantorsfakturor',
    label: 'Leverant\u00f6rsfakturor (Fakturor ut)',
    description: 'Kostnader \u2014 fakturor du tagit emot och betalat',
    accent: '#ef4444',
  },
  {
    id: 'utlagg',
    label: 'Utl\u00e4gg / Reser\u00e4kningar',
    description: 'Personalutl\u00e4gg \u2014 kvitton betalda privat som ers\u00e4tts av bolaget',
    accent: '#3b82f6',
  },
]

interface SiePreview {
  companyName?: string
  fiscalYearStart?: string
  fiscalYearEnd?: string
  rowCount: number
  warnings: string[]
  months: { month: number; vatIn: number; vatOut: number; income: number; expense: number }[]
}

export function Import({ onClose }: { onClose: () => void }) {
  const { selected, companies, setSelectedId } = useSelectedCompany()
  const { year, availableYears, setYear } = useYear()
  const [siePreview, setSiePreview] = useState<SiePreview | null>(null)
  const [sieExisting, setSieExisting] = useState<CloudYearDoc | null>(null)
  const [mergeMode, setMergeMode] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const sieInputRef = useRef<HTMLInputElement>(null)
  // One-time Dropbox JSON-import (fix 90k gap)
  const [showJsonImport, setShowJsonImport] = useState(false)

  // Duplicate scan state
  const [scanning, setScanning] = useState(false)
  const [scanResult, setScanResult] = useState<{ groups: DupScanGroup[]; total: number; date: string } | null>(null)
  const [lastScanDate, setLastScanDate] = useState<string | null>(null)
  // Side-by-side duplicate comparison
  const [compareGroup, setCompareGroup] = useState<DupScanGroup | null>(null)

  useEffect(() => {
    try {
      return subscribeYear(selected.id, year, (d) => {
        setLastScanDate(d.dupScanDate ?? null)
      })
    } catch { /* ignore */ }
  }, [selected.id, year])

  async function runDupScan() {
    setScanning(true)
    setScanResult(null)
    try {
      const groups = await scanAllDuplicates(selected.id, year)
      const total = groups.reduce((s, g) => s + g.receipts.length, 0)
      const now = new Date().toISOString()
      await saveDupScanTimestamp(selected.id, year)
      setScanResult({ groups, total, date: now })
      setLastScanDate(now)
    } catch (e) {
      setStatus('Duplikatscan misslyckades: ' + (e as Error).message)
    } finally {
      setScanning(false)
    }
  }

  const [dupWarning, setDupWarning] = useState<{
    file: File
    category: ReceiptCategory
    matches: DuplicateMatch[]
    remaining: { file: File; category: ReceiptCategory }[]
    uploaded: number
    total: number
    warnings: string[]
  } | null>(null)

  async function handleSieSelected(file: File) {
    setStatus(null)
    const text = await file.text()
    const parsed = parseSie(text, year)
    setSiePreview(parsed)

    try {
      const existing = await new Promise<CloudYearDoc>((resolve) => {
        const unsub = subscribeYear(selected.id, year, (d) => {
          unsub()
          resolve(d)
        })
      })
      const hasData = existing.months.some(
        (m) => m.vatIn !== 0 || m.vatOut !== 0 || m.income !== 0 || m.expense !== 0
      )
      setSieExisting(hasData ? existing : null)
    } catch {
      setSieExisting(null)
    }
  }

  async function applySie() {
    if (!siePreview) return
    try {
      setStatus('Skriver till Firestore...')
      await applyMonthsCloud(selected.id, year, siePreview.months, mergeMode)
      // Feature 1: SIE-importen tcker alla 3 kategorier samtidigt \u2014 vi
      // skriver samma timestamp pa alla tre med source='sie-import' och
      // rowCount som fileCount (proxy fr antal transaktioner).
      try {
        const cats: ReceiptCategory[] = ['kundfakturor', 'leverantorsfakturor', 'utlagg']
        for (const c of cats) {
          await recordSync(selected.id, year, c, siePreview.rowCount, 'sie-import')
        }
      } catch (e) { console.warn('recordSync (sie) failed', e) }
      setStatus(
        '\u2714\uFE0F Skrev ' + siePreview.rowCount + ' rader till ' + selected.name + ' for ' + year + ' ' +
          '(' + (mergeMode ? 'adderade' : 'ersatte') + ' varden). Dashboarden uppdateras automatiskt.'
      )
      setSiePreview(null)
      setSieExisting(null)
      if (sieInputRef.current) sieInputRef.current.value = ''
    } catch (e) {
      setStatus('\u274C Fel: ' + (e as Error).message)
    }
  }

  // AI analysis state
  // Year-change flash effect
  const yearSelectRef = useRef<HTMLSelectElement>(null)
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [yearFlash, setYearFlash] = useState(false)

  function changeYearWithFlash(y: number) {
    setYear(y)
    setYearFlash(true)
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
    flashTimerRef.current = setTimeout(() => setYearFlash(false), 5000)
    // Scroll to top so user sees the drop zones for the new year
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // --- Upload pipeline ---
  interface PipelineEntry {
    id: string                       // receipt Firestore ID
    filename: string
    uploaded: boolean
    xai: 'pending' | 'running' | 'done' | 'error'
    duplikat: 'pending' | 'clean' | 'warn' | 'skipped'
    dupMatches?: DuplicateMatch[]
    dataOk: boolean                  // computed from quality
    verified: boolean                // quality === 'verified'
  }
  const [pipeline, setPipeline] = useState<PipelineEntry[]>([])
  const [pipelineReview, setPipelineReview] = useState<string | null>(null) // receipt id to review
  // Live receipts for this year (used to update pipeline quality in real-time)
  const [liveReceipts, setLiveReceipts] = useState<CloudReceipt[]>([])

  useEffect(() => {
    try {
      return subscribeReceipts(selected.id, year, setLiveReceipts)
    } catch { return () => {} }
  }, [selected.id, year])

  // Update pipeline entries when live receipts change
  useEffect(() => {
    if (pipeline.length === 0 || liveReceipts.length === 0) return
    setPipeline((prev) => prev.map((entry) => {
      const r = liveReceipts.find((lr) => lr.id === entry.id)
      if (!r) return entry
      const q = r.quality || computeQuality(r)
      return {
        ...entry,
        dataOk: q === 'green' || q === 'verified' || q === 'yellow',
        verified: q === 'verified',
      }
    }))
  }, [liveReceipts])

  // Legacy compat: keep old aiQueue/aiDone for the status bar
  const [aiQueue, setAiQueue] = useState<{ id: string; name: string }[]>([])
  const [aiDone, setAiDone] = useState<{ name: string; ok: boolean; info?: string }[]>([])

  const uploadOneFile = useCallback(async (
    file: File,
    cat: ReceiptCategory
  ): Promise<{ ok: boolean; warning?: string; receiptId?: string }> => {
    try {
      const receipt = await uploadReceiptCloud(selected.id, cat, file, year)

      // Add to pipeline as uploaded
      setPipeline((prev) => [...prev, {
        id: receipt.id,
        filename: file.name,
        uploaded: true,
        xai: 'running',
        duplikat: 'pending',
        dataOk: false,
        verified: false,
      }])

      // Auto-trigger AI analysis in background
      const entry = { id: receipt.id, name: file.name }
      setAiQueue((q) => [...q, entry])
      aiAnalyzeReceipt(selected.id, receipt).then((res) => {
        setAiQueue((q) => q.filter((e) => e.id !== entry.id))
        setAiDone((d) => [...d, {
          name: file.name,
          ok: res.updated,
          info: res.skipped ? 'Komplett \u2014 hoppade \u00f6ver' : res.updated ? 'AI fyllde i f\u00e4lt' : (res.error ?? 'inget nytt'),
        }])
        // Update pipeline xai status
        setPipeline((prev) => prev.map((e) =>
          e.id === receipt.id ? { ...e, xai: 'done' } : e
        ))
      }).catch((e) => {
        console.warn('[Import] aiAnalyze per-file catch (non-fatal, marked error in UI):', e)
        setAiQueue((q) => q.filter((e) => e.id !== entry.id))
        setAiDone((d) => [...d, { name: file.name, ok: false, info: 'AI-fel' }])
        setPipeline((prev) => prev.map((e) =>
          e.id === receipt.id ? { ...e, xai: 'error' } : e
        ))
      })
      return { ok: true, receiptId: receipt.id }
    } catch (e) {
      return { ok: false, warning: file.name + ': ' + (e as Error).message }
    }
  }, [selected.id, year])

  async function handlePdfsSelected(files: FileList | File[] | null, cat: ReceiptCategory) {
    if (!files || (Array.isArray(files) ? files.length === 0 : files.length === 0)) return
    const arr = Array.isArray(files) ? files : Array.from(files)
    await processUploadQueue(arr.map((f) => ({ file: f, category: cat })), 0, arr.length, [])
  }

  async function processUploadQueue(
    queue: { file: File; category: ReceiptCategory }[],
    uploaded: number,
    total: number,
    warnings: string[]
  ) {
    // Feature 1: rkna antalet faktiskt uppladdade filer per kategori sa vi
    // kan skriva en recordSync() per kategori nr ken r klar.
    const byCat: Record<ReceiptCategory, number> = { kundfakturor: 0, leverantorsfakturor: 0, utlagg: 0 }

    for (let i = 0; i < queue.length; i++) {
      const { file, category } = queue[i]
      setStatus('Kontrollerar duplikat ' + (uploaded + i + 1) + '/' + total + ': ' + file.name + '...')

      try {
        const meta = parseFilename(file.name)
        const matches = await checkDuplicates(selected.id, year, file, {
          date: meta.date,
          amount: meta.amount,
          filename: file.name,
        })

        if (matches.length > 0) {
          setDupWarning({
            file,
            category,
            matches,
            remaining: queue.slice(i + 1),
            uploaded: uploaded + i,
            total,
            warnings: [...warnings],
          })
          // Spara recordSync fr det som hunnit laddas upp innan popup-paus
          await flushSyncStatus(byCat)
          return
        }
      } catch {
        // If duplicate check fails, upload anyway
      }

      setStatus('Laddar upp ' + (uploaded + i + 1) + '/' + total + ': ' + file.name + '...')
      const res = await uploadOneFile(file, category)
      if (!res.ok && res.warning) warnings.push(res.warning)
      // Mark duplicate check as clean in pipeline
      if (res.ok && res.receiptId) {
        byCat[category] = (byCat[category] ?? 0) + 1
        setPipeline((prev) => prev.map((e) =>
          e.id === res.receiptId ? { ...e, duplikat: 'clean' } : e
        ))
      }
    }

    // Feature 1: spara senast-syncad-status fr varje kategori som faktiskt
    // fick filer i denna batch.
    await flushSyncStatus(byCat)

    const okCount = total - warnings.length
    const warn = warnings.length > 0 ? '\nFel:\n\u2022 ' + warnings.slice(0, 5).join('\n\u2022 ') : ''
    setStatus(
      '\u2714\uFE0F ' + okCount + '/' + total + ' filer uppladdade till Firebase Storage. ' +
        'Syns live pa dashboarden och i alla oppna flikar.' + warn
    )
  }

  async function flushSyncStatus(byCat: Record<ReceiptCategory, number>) {
    for (const [cat, n] of Object.entries(byCat)) {
      if (n > 0) {
        try { await recordSync(selected.id, year, cat as ReceiptCategory, n, 'manual-upload') }
        catch (e) { console.warn('recordSync failed for', cat, e) }
      }
    }
  }

  async function onDupUploadAnyway() {
    if (!dupWarning) return
    const { file, category, remaining, uploaded, total, warnings, matches } = dupWarning
    setDupWarning(null)
    setStatus('Laddar upp ' + (uploaded + 1) + '/' + total + ': ' + file.name + '...')
    const res = await uploadOneFile(file, category)
    // Mark as duplicate warning in pipeline
    if (res.ok && res.receiptId) {
      setPipeline((prev) => prev.map((e) =>
        e.id === res.receiptId ? { ...e, duplikat: 'warn', dupMatches: matches } : e
      ))
      // Feature 1: rkna in dubblettuppladdningen i sync-status
      try { await recordSync(selected.id, year, category, 1, 'manual-upload') }
      catch (e) { console.warn('recordSync failed', e) }
    }
    const w = [...warnings]
    if (!res.ok && res.warning) w.push(res.warning)
    await processUploadQueue(remaining, uploaded + 1, total, w)
  }

  function onDupSkip() {
    if (!dupWarning) return
    const { remaining, uploaded, total, warnings } = dupWarning
    setDupWarning(null)
    processUploadQueue(remaining, uploaded + 1, total, [...warnings, dupWarning.file.name + ': hoppade over (duplikat)'])
  }

  function onDupAbort() {
    if (!dupWarning) return
    const { uploaded, total, warnings } = dupWarning
    const w = [...warnings, dupWarning.file.name + ': avbruten av anvandare']
    const skipped = total - uploaded - 1
    setDupWarning(null)
    setStatus(
      'Avbrot. ' + uploaded + '/' + total + ' uppladdade' +
        (skipped > 0 ? ', ' + (skipped + 1) + ' hoppades over' : '') +
        (w.length > 0 ? '\n\u2022 ' + w.join('\n\u2022 ') : '')
    )
  }

  return (
    <div className="shell">
      <header className="topbar" style={{ position: 'sticky', top: 0, zIndex: 100, background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
        <div className="topbar__brand">Import</div>
        <div className="topbar__right">
          <select
            value={selected.id}
            onChange={(e) => setSelectedId(e.target.value)}
            style={{
              background: 'var(--bg-card)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '0.4rem 0.7rem',
              fontSize: '0.95rem',
            }}
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            ref={yearSelectRef}
            value={year}
            onChange={(e) => changeYearWithFlash(Number(e.target.value))}
            style={{
              background: yearFlash ? 'rgba(239,68,68,0.15)' : 'var(--bg-card)',
              color: yearFlash ? '#ef4444' : 'var(--text)',
              border: yearFlash ? '2px solid #ef4444' : '1px solid var(--border)',
              borderRadius: 6,
              padding: '0.4rem 0.7rem',
              fontSize: yearFlash ? '1rem' : '0.95rem',
              fontWeight: yearFlash ? 700 : 400,
              transition: 'all 0.3s ease, background 1.5s ease, border-color 1.5s ease',
            }}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {'\u00c5r ' + y}
              </option>
            ))}
          </select>
          <SyncStatusCard companyId={selected.id} year={year} compact />
          <button
            className="logout-btn"
            onClick={() => setShowJsonImport(true)}
            style={{ fontWeight: 600 }}
            title="Importera poster fr\u00e5n Dropbox-skanner-JSON (engangs)"
          >
            {'\ud83d\udcc1 Importera Dropbox-JSON'}
          </button>
          <button className="logout-btn" onClick={onClose}>
            {'\u2190 Tillbaka'}
          </button>
        </div>
      </header>

      {status && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            whiteSpace: 'pre-line',
            fontSize: '0.9rem',
          }}
        >
          {status}
        </div>
      )}

      {/* Senast syncad per kategori — Feature 1 */}
      <SyncStatusCard companyId={selected.id} companyName={selected.name} year={year} />

      {/* Manuell "Synka nu" → bolag-autosync-worker (POST /sync/run) */}
      <RunNowButton />

      {/* PDF batch — placerad ÖVER Uppladdningspipeline så användaren ser
          drop-zonerna direkt utan att behöva scrolla förbi logglistan.
          2026-05-21 layout-fix. */}
      <section style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          {'Massimport PDF — per bokföringskategori (' + year + ')'}
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          {'Släpp PDF-filer eller mappar i motsvarande ruta. Filnamn enligt standard '}
          <code>{'YYYY-MM-DD - artikel - belopp - VALUTA.pdf'}</code>
          {' parsas automatiskt — datum styr vilken månad posten hamnar i.'}
        </p>
        <div className="grid">
          {CATEGORIES.map((c) => (
            <DropZone
              key={c.id}
              label={c.label}
              description={c.description}
              accent={c.accent}
              onFiles={(files) => handlePdfsSelected(files, c.id)}
            />
          ))}
        </div>
      </section>

      {/* Momsbalans-kort med "Kör AI igen"-knapp — placerat över pipelinen
          så användaren kan starta om-körningar direkt från Import-vyn utan
          att navigera till Dashboard. 2026-05-21. */}
      <div style={{ marginBottom: '1rem' }}>
        <MomsBalanceCard companyId={selected.id} companyName={selected.name} />
      </div>

      {/* Upload pipeline — 5-steg checklist per fil */}
      {pipeline.length > 0 && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Uppladdningspipeline</div>
            <button
              onClick={() => setPipeline([])}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.7rem', cursor: 'pointer' }}
            >Rensa</button>
          </div>
          {/* Column headers */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem', paddingLeft: 0 }}>
            <span style={{ flex: 1, fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Fil</span>
            {['Uppladdad', 'xAI', 'Dublett', 'Data OK', 'Verifierad'].map((h) => (
              <span key={h} style={{ width: 56, textAlign: 'center', fontSize: '0.55rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{h}</span>
            ))}
          </div>
          {/* Pipeline rows */}
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {pipeline.map((entry) => (
              <PipelineRow
                key={entry.id}
                entry={entry}
                onReview={() => setPipelineReview(entry.id)}
              />
            ))}
          </div>
          {/* Summary - rakanrna uppdateras vid varje state-change automatiskt (derived fran pipeline-arrayen) */}
          {(() => {
            const total = pipeline.length
            const verifiedCount = pipeline.filter((e) => e.verified).length
            const aiRunningCount = pipeline.filter((e) => e.xai === 'running' || e.xai === 'pending').length
            const aiDoneCount = total - aiRunningCount
            const missingDataCount = pipeline.filter((e) => !e.dataOk && e.xai !== 'running' && e.xai !== 'pending').length
            const aiPct = total > 0 ? Math.round((aiDoneCount / total) * 100) : 0
            const dataPct = total > 0 ? Math.round(((total - missingDataCount) / total) * 100) : 0
            return (
              <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span>{total + ' filer'}</span>
                <span style={{ color: '#10b981' }}>{verifiedCount + ' verifierade'}</span>
                {/* xAI progress */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#eab308' }}>
                  <span>{aiRunningCount + ' AI p\u00e5g\u00e5r'}</span>
                  <span
                    title={'xAI klar: ' + aiDoneCount + '/' + total + ' (' + aiPct + '%)'}
                    style={{ display: 'inline-block', width: 80, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden', verticalAlign: 'middle' }}
                  >
                    <span style={{ display: 'block', height: '100%', width: aiPct + '%', background: '#eab308', transition: 'width 0.3s ease' }} />
                  </span>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{aiDoneCount + '/' + total}</span>
                </span>
                {/* Saknar data progress */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#f59e0b' }}>
                  <span>{missingDataCount + ' saknar data'}</span>
                  <span
                    title={'Data OK: ' + (total - missingDataCount) + '/' + total + ' (' + dataPct + '%)'}
                    style={{ display: 'inline-block', width: 80, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden', verticalAlign: 'middle' }}
                  >
                    <span style={{ display: 'block', height: '100%', width: dataPct + '%', background: '#10b981', transition: 'width 0.3s ease' }} />
                  </span>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{(total - missingDataCount) + '/' + total}</span>
                </span>
              </div>
            )
          })()}
        </div>
      )}

      {/* Pipeline review popup */}
      {pipelineReview && (() => {
        const receipt = liveReceipts.find((r) => r.id === pipelineReview)
        if (!receipt) return null
        return (
          <PipelineReviewPopup
            receipt={receipt}
            companyId={selected.id}
            onClose={() => setPipelineReview(null)}
            onNext={() => {
              // Find next unverified in pipeline
              const idx = pipeline.findIndex((e) => e.id === pipelineReview)
              const next = pipeline.slice(idx + 1).find((e) => !e.verified && !e.dataOk)
              if (next) setPipelineReview(next.id)
              else setPipelineReview(null)
            }}
          />
        )
      })()}

      {/* Duplikat-varning vid upload \u2014 Feature 2: side-by-side PDF-jmfrelse */}
      {dupWarning && (
        <DuplicateComparePopup
          mode="new-upload"
          incomingFile={dupWarning.file}
          incomingMeta={{
            filename: dupWarning.file.name,
            size:     dupWarning.file.size,
            date:     parseFilename(dupWarning.file.name).date,
            amount:   parseFilename(dupWarning.file.name).amount,
            currency: parseFilename(dupWarning.file.name).currency,
          }}
          matches={dupWarning.matches}
          onUploadAnyway={onDupUploadAnyway}
          onSkip={onDupSkip}
          onAbort={onDupAbort}
          progress={{ current: dupWarning.uploaded + 1, total: dupWarning.total }}
        />
      )}

      {/* PDF batch — dubblett-block borttaget 2026-05-21 (Massimport renderas över Uppladdningspipeline). */}

      {/* Importöversikt — alla år */}
      <ImportOverview
        companyId={selected.id}
        companyName={selected.name}
        selectedYear={year}
        onSelectYear={changeYearWithFlash}
      />

      {/* SIE-import */}
      <section className="card" style={{ marginBottom: '1rem' }}>
        <div className="card__label">{'Importera fr\u00e5n SIE-fil (gamla \u00e5r, bokf\u00f6ringsexport)'}</div>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
          {'SIE (Standard Import Export) \u00e4r svensk bokf\u00f6ringsstandard \u2014 exporteras fr\u00e5n Fortnox, Visma, Bj\u00f6rn Lund\u00e9n m.fl. V\u00e4lj '}
          <b>SIE4-format</b>
          {' (inneh\u00e5ller verifikat och transaktioner). Datat aggregeras till m\u00e5nadssummor och skriver till valt \u00e5r (' + year + ').'}
        </p>
        <input
          ref={sieInputRef}
          type="file"
          accept=".se,.sie,.si,.txt,text/plain"
          onChange={(e) => e.target.files?.[0] && handleSieSelected(e.target.files[0])}
          style={{ marginTop: '0.5rem' }}
        />

        {siePreview && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              {siePreview.companyName && <><b>{'Bolag i fil: ' + siePreview.companyName}</b>{' \u00b7 '}</>}
              {'R\u00e4kenskaps\u00e5r: ' + (siePreview.fiscalYearStart ?? '?') + ' \u2192 ' + (siePreview.fiscalYearEnd ?? '?') + ' \u00b7 Transaktioner: '}
              <b>{siePreview.rowCount}</b>
            </div>
            {siePreview.warnings.length > 0 && (
              <div style={{ color: 'var(--warning)', fontSize: '0.85rem', marginTop: 4 }}>
                {'\u26A0 ' + siePreview.warnings.join(' \u2014 ')}
              </div>
            )}
            {sieExisting && (
              <div
                style={{
                  background: 'rgba(245,158,11,0.1)',
                  border: '1px solid var(--warning)',
                  borderRadius: 6,
                  padding: '0.6rem 0.8rem',
                  marginTop: 8,
                  fontSize: '0.85rem',
                }}
              >
                <b style={{ color: 'var(--warning)' }}>{'OBS: ' + selected.name + ' har redan data f\u00f6r ' + year}</b>
                <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                  {'Befintliga m\u00e5nadsv\u00e4rden: ' + sieExisting.months.filter(
                    (m) => m.vatIn !== 0 || m.vatOut !== 0 || m.income !== 0 || m.expense !== 0
                  ).length + ' m\u00e5nader med data. V\u00e4lj "Addera" f\u00f6r att l\u00e4gga till, eller l\u00e4mna avbockad f\u00f6r att ers\u00e4tta.'}
                </div>
              </div>
            )}
            <table style={{ width: '100%', marginTop: '0.75rem', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', opacity: 0.7 }}>
                  <th style={{ padding: '4px 8px' }}>M\u00e5nad</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Moms in</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Moms ut</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Int\u00e4kter</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Kostnader</th>
                </tr>
              </thead>
              <tbody>
                {siePreview.months.map((m, i) => {
                  if (m.vatIn === 0 && m.vatOut === 0 && m.income === 0 && m.expense === 0) return null
                  return (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '4px 8px' }}>{MONTHS[i]}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmt(m.vatIn)}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmt(m.vatOut)}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--success)' }}>{fmt(m.income)}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--danger)' }}>{fmt(m.expense)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                <input
                  type="checkbox"
                  checked={mergeMode}
                  onChange={(e) => setMergeMode(e.target.checked)}
                  style={{ marginRight: 4 }}
                />
                {'Addera till befintliga v\u00e4rden (annars ers\u00e4tt)'}
              </label>
              <button className="login__btn" style={{ width: 'auto', padding: '0.5rem 1rem' }} onClick={applySie}>
                {'Skriv till ' + year}
              </button>
              <button className="logout-btn" onClick={() => setSiePreview(null)}>
                Avbryt
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Duplikatkontroll */}
      <section className="card" style={{ marginBottom: '1rem' }}>
        <div className="card__header-row">
          <div className="card__label" style={{ margin: 0 }}>{'Duplikatkontroll \u2014 ' + selected.name + ' (' + year + ')'}</div>
          <button
            className="logout-btn"
            style={{ fontWeight: 600 }}
            onClick={runDupScan}
            disabled={scanning}
          >
            {scanning ? 'Skannar...' : 'K\u00f6r duplikatkontroll'}
          </button>
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
          {lastScanDate
            ? 'Senaste kontroll: ' + new Date(lastScanDate).toLocaleString('sv-SE')
            : 'Ingen duplikatkontroll har k\u00f6rts f\u00f6r detta bolag och \u00e5r.'}
        </div>

        {scanResult && (
          <div style={{ marginTop: '0.75rem' }}>
            {scanResult.groups.length === 0 ? (
              <div style={{
                background: 'rgba(16,185,129,0.1)',
                border: '1px solid var(--success)',
                borderRadius: 6,
                padding: '0.6rem 0.8rem',
                fontSize: '0.9rem',
                color: 'var(--success)',
              }}>
                Inga duplikater hittades bland kvittona.
              </div>
            ) : (
              <div>
                <div style={{
                  background: 'rgba(245,158,11,0.1)',
                  border: '1px solid var(--warning)',
                  borderRadius: 6,
                  padding: '0.6rem 0.8rem',
                  fontSize: '0.9rem',
                  marginBottom: '0.5rem',
                }}>
                  <b style={{ color: 'var(--warning)' }}>
                    {scanResult.groups.length + ' duplikatgrupp' + (scanResult.groups.length > 1 ? 'er' : '') + ' hittade (' + scanResult.total + ' kvitton)'}
                  </b>
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {scanResult.groups.map((g, gi) => (
                    <div
                      key={gi}
                      onClick={() => setCompareGroup(g)}
                      style={{
                        background: 'var(--bg-card-alt)',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        padding: '0.5rem 0.7rem',
                        marginBottom: '0.4rem',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'rgba(59,130,246,0.06)' }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card-alt)' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                        <span style={{
                          background: g.reason === 'exact-hash' ? 'var(--danger)' : g.reason === 'same-filename' ? 'var(--warning)' : 'var(--primary)',
                          color: '#fff',
                          padding: '2px 6px',
                          borderRadius: 4,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                        }}>
                          {g.reason === 'exact-hash' ? 'Identiska filer' : g.reason === 'same-filename' ? 'Samma filnamn' : 'Liknande belopp+datum'}
                        </span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          {g.receipts.length + ' kvitton'}
                        </span>
                        <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--primary)' }}>
                          {'J\u00e4mf\u00f6r \u25b6'}
                        </span>
                      </div>
                      {g.receipts.map((r) => (
                        <div key={r.id} style={{
                          display: 'flex',
                          gap: '0.5rem',
                          alignItems: 'center',
                          fontSize: '0.82rem',
                          padding: '0.2rem 0',
                          borderTop: '1px solid var(--border)',
                        }}>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.filename}>
                            {receiptListTitle(r)}
                          </span>
                          {r.date && <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{r.date}</span>}
                          {r.amount != null && (
                            <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                              {fmt(r.amount) + ' ' + (r.currency ?? '')}
                            </span>
                          )}
                          {r.downloadUrl && (
                            <a href={r.downloadUrl} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                              {'\u00d6ppna'}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Side-by-side duplikatjmfrelse popup \u2014 Feature 3: existing-group mode */}
      {compareGroup && (
        <DuplicateComparePopup
          mode="existing-group"
          group={compareGroup}
          companyId={selected.id}
          onClose={() => setCompareGroup(null)}
          onDeleted={(id) => {
            // Remove deleted receipt from scan results
            setScanResult((prev) => {
              if (!prev) return prev
              const groups = prev.groups
                .map((g) => ({ ...g, receipts: g.receipts.filter((r) => r.id !== id) }))
                .filter((g) => g.receipts.length >= 2)
              const total = groups.reduce((s, g) => s + g.receipts.length, 0)
              return { ...prev, groups, total }
            })
            // Also remove from compare group
            setCompareGroup((prev) => {
              if (!prev) return prev
              const receipts = prev.receipts.filter((r) => r.id !== id)
              if (receipts.length < 2) return null
              return { ...prev, receipts }
            })
          }}
          onKeepBoth={() => {
            // Markera gruppen som hanterad \u2014 ta bort frn scanResult sa anvndaren
            // ser progress framt.
            setScanResult((prev) => {
              if (!prev || !compareGroup) return prev
              const groups = prev.groups.filter((g) => g !== compareGroup)
              const total = groups.reduce((s, g) => s + g.receipts.length, 0)
              return { ...prev, groups, total }
            })
          }}
        />
      )}

      {/* One-time Dropbox JSON-import (fix 90k gap) */}
      <JsonImportDialog
        open={showJsonImport}
        onClose={() => setShowJsonImport(false)}
      />

    </div>
  )
}

// ============================================================
// Pipeline row — 5 färgade checkboxar per uppladdad fil
// ============================================================

const STEP_COLORS = {
  green: '#10b981',
  red: '#f59e0b',
  yellow: '#eab308',
  gray: 'rgba(255,255,255,0.15)',
  blue: '#3b82f6',
}

interface PipelineRowEntry {
  id: string
  filename: string
  uploaded: boolean
  xai: 'pending' | 'running' | 'done' | 'error'
  duplikat: 'pending' | 'clean' | 'warn' | 'skipped'
  dataOk: boolean
  verified: boolean
}

function PipelineRow({ entry, onReview }: { entry: PipelineRowEntry; onReview: () => void }) {
  function dot(color: string, label: string, pulse?: boolean) {
    return (
      <span title={label} style={{
        width: 56, display: 'flex', justifyContent: 'center',
      }}>
        <span style={{
          width: 14, height: 14, borderRadius: '50%',
          background: color, display: 'inline-block',
          border: '2px solid ' + (color === STEP_COLORS.gray ? 'rgba(255,255,255,0.08)' : color),
          boxShadow: pulse ? '0 0 6px ' + color : 'none',
          animation: pulse ? 'pulse 1.5s infinite' : 'none',
          transition: 'background 0.3s, box-shadow 0.3s',
        }} />
      </span>
    )
  }

  function clickableDot(color: string, icon: string, label: string, onClick: () => void) {
    return (
      <span title={label} style={{ width: 56, display: 'flex', justifyContent: 'center' }}>
        <span
          onClick={(e) => { e.stopPropagation(); onClick() }}
          style={{
            width: 18, height: 18, borderRadius: '50%',
            background: color, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: '0.6rem', color: '#fff', fontWeight: 700,
            cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
            border: '2px solid ' + color,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.3)'; e.currentTarget.style.boxShadow = '0 0 8px ' + color }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none' }}
        >
          {icon}
        </span>
      </span>
    )
  }

  // 1. Uppladdad
  const step1 = entry.uploaded ? dot(STEP_COLORS.green, 'Uppladdad') : dot(STEP_COLORS.gray, 'V\u00e4ntar')

  // 2. xAI
  const step2 = entry.xai === 'done' ? dot(STEP_COLORS.green, 'xAI klar')
    : entry.xai === 'running' ? dot(STEP_COLORS.yellow, 'xAI analyserar...', true)
    : entry.xai === 'error' ? dot(STEP_COLORS.red, 'xAI fel')
    : dot(STEP_COLORS.gray, 'xAI v\u00e4ntar')

  // 3. Dublett
  const step3 = entry.duplikat === 'clean' ? dot(STEP_COLORS.green, 'Ingen duplikat')
    : entry.duplikat === 'warn' ? dot(STEP_COLORS.yellow, 'M\u00f6jlig duplikat')
    : entry.duplikat === 'skipped' ? dot(STEP_COLORS.red, 'Hoppade \u00f6ver (duplikat)')
    : dot(STEP_COLORS.gray, 'Ej kontrollerad')

  // 4. Data OK
  const step4 = entry.dataOk ? dot(STEP_COLORS.green, 'Data komplett')
    : (entry.xai === 'done' || entry.xai === 'error') ? dot(STEP_COLORS.red, 'Saknar data')
    : dot(STEP_COLORS.gray, 'V\u00e4ntar p\u00e5 AI')

  // 5. Verifierad — klickbar om inte redan grön
  const step5 = entry.verified
    ? clickableDot(STEP_COLORS.green, '\u2713', 'Manuellt verifierad — klicka f\u00f6r att granska', onReview)
    : entry.dataOk
      ? clickableDot(STEP_COLORS.blue, '\u270e', 'Klicka f\u00f6r att verifiera', onReview)
      : clickableDot(STEP_COLORS.red, '\u270e', 'Klicka f\u00f6r att fixa data och verifiera', onReview)

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.4rem',
      padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)',
    }}>
      <span style={{
        flex: 1, fontSize: '0.8rem', overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        color: entry.verified ? 'var(--text-muted)' : '#fff',
      }} title={entry.filename}>
        {entry.filename}
      </span>
      {step1}{step2}{step3}{step4}{step5}
    </div>
  )
}

// ============================================================
// Pipeline review popup — snabb verifiering direkt fr\u00e5n pipeline
// ============================================================

const P_FIELD: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
  borderRadius: 6, padding: '0.45rem 0.6rem', fontSize: '0.9rem',
  color: '#fff', width: '100%', outline: 'none',
}
const P_LABEL: React.CSSProperties = {
  display: 'block', fontSize: '0.65rem', textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 2,
}

function PipelineReviewPopup({ receipt, companyId, onClose, onNext }: {
  receipt: CloudReceipt
  companyId: string
  onClose: () => void
  onNext: () => void
}) {
  const [date, setDate] = useState(receipt.date ?? '')
  const [title, setTitle] = useState(receipt.title ?? '')
  const [amount, setAmount] = useState(receipt.amount != null ? String(receipt.amount) : '')
  const [amountExVat, setAmountExVat] = useState(receipt.amountExVat != null ? String(receipt.amountExVat) : '')
  const [vatAmount, setVatAmount] = useState(receipt.vatAmount != null ? String(receipt.vatAmount) : '')
  const [vatRate, setVatRate] = useState(receipt.vatRate != null ? String(receipt.vatRate * 100) : '')
  const [currency, setCurrency] = useState(receipt.currency ?? 'SEK')
  const [accountCode, setAccountCode] = useState(receipt.accountCode != null ? String(receipt.accountCode) : '')
  const [accountLabel, setAccountLabel] = useState(receipt.accountLabel ?? '')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const [splitPct, setSplitPct] = useState(60)
  const [isDragging, setIsDragging] = useState(false)
  const dragging = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  function onDragStart(e: React.MouseEvent) {
    e.preventDefault()
    dragging.current = true
    setIsDragging(true)
    const onMove = (ev: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const pct = ((ev.clientX - rect.left) / rect.width) * 100
      setSplitPct(Math.min(80, Math.max(25, pct)))
    }
    const onUp = () => { dragging.current = false; setIsDragging(false); document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  useEffect(() => {
    setDate(receipt.date ?? '')
    setTitle(receipt.title ?? '')
    setAmount(receipt.amount != null ? String(receipt.amount) : '')
    setAmountExVat(receipt.amountExVat != null ? String(receipt.amountExVat) : '')
    setVatAmount(receipt.vatAmount != null ? String(receipt.vatAmount) : '')
    setVatRate(receipt.vatRate != null ? String(receipt.vatRate * 100) : '')
    setCurrency(receipt.currency ?? 'SEK')
    setAccountCode(receipt.accountCode != null ? String(receipt.accountCode) : '')
    setAccountLabel(receipt.accountLabel ?? '')
    setMsg(null)
  }, [receipt.id])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const parseNum = (v: string) => {
    const n = parseFloat(v.replace(/\s/g, '').replace(',', '.'))
    return isNaN(n) ? undefined : n
  }

  function autoCalcVat(newAmount?: string, newRate?: string) {
    const amt = parseNum(newAmount ?? amount)
    let rate = parseNum(newRate ?? vatRate)
    if (rate != null && rate >= 1) rate = rate / 100
    if (amt != null && rate != null && rate > 0) {
      const vat = amt * rate / (1 + rate)
      setVatAmount(vat.toFixed(2))
      setAmountExVat((amt - vat).toFixed(2))
    }
  }

  async function onSave(andNext: boolean) {
    setSaving(true)
    setMsg(null)
    try {
      let rateVal = parseNum(vatRate)
      if (rateVal != null && rateVal >= 1) rateVal = rateVal / 100
      const accNum = parseNum(accountCode)
      await updateReceiptFields(companyId, receipt.id, {
        date: date || undefined,
        title: title || undefined,
        amount: parseNum(amount),
        amountExVat: parseNum(amountExVat),
        vatAmount: parseNum(vatAmount),
        vatRate: rateVal,
        currency: currency || undefined,
        accountCode: accNum != null ? Math.round(accNum) : undefined,
        accountLabel: accountLabel || undefined,
      })
      if (andNext) onNext()
      else { setMsg('Sparat och verifierat!'); setTimeout(() => setMsg(null), 2000) }
    } catch (e) {
      setMsg('Fel: ' + (e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const isImage = receipt.mime?.startsWith('image/')
  const isPdf = receipt.mime === 'application/pdf'
  const q = receipt.quality || computeQuality(receipt)
  const missing: string[] = []
  if (!receipt.date) missing.push('datum')
  if (!receipt.title) missing.push('beskrivning')
  if (receipt.amount == null) missing.push('belopp')
  if (receipt.vatRate == null) missing.push('momssats')
  if (receipt.accountCode == null) missing.push('konto')

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column', zIndex: 10000 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, margin: '0.5rem', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 1rem', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ fontSize: '1rem', fontWeight: 700 }}>Verifiera</div>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{receipt.filename}</span>
            {missing.length > 0 && <span style={{ fontSize: '0.75rem', color: '#f97316', background: 'rgba(249,115,22,0.1)', padding: '2px 8px', borderRadius: 4 }}>{'Saknar: ' + missing.join(', ')}</span>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '1.3rem', cursor: 'pointer', padding: '0.25rem' }}>{'\u2715'}</button>
        </div>

        {/* Body */}
        <div ref={containerRef} style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
          {isDragging && <div style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'col-resize' }} />}
          {/* Left: preview */}
          <div style={{ width: splitPct + '%', flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#111827', overflow: 'hidden' }}>
            {receipt.downloadUrl ? (
              isPdf ? <iframe src={receipt.downloadUrl} title={receipt.filename} style={{ flex: 1, border: 'none', width: '100%' }} />
              : isImage ? <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflow: 'auto' }}><img src={receipt.downloadUrl} alt={receipt.filename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }} /></div>
              : <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}><a href={receipt.downloadUrl} target="_blank" rel="noopener" style={{ color: 'var(--primary)' }}>{'\u00f6ppna \u2197'}</a></div>
            ) : <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Ingen fil</div>}
          </div>

          {/* Divider */}
          <div onMouseDown={onDragStart} style={{ width: 12, cursor: 'col-resize', background: 'transparent', flexShrink: 0, position: 'relative', zIndex: 3 }}>
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 4, background: isDragging ? 'var(--primary)' : 'var(--border)', transition: 'background 0.15s' }} />
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 6, height: 48, borderRadius: 3, background: isDragging ? 'var(--primary)' : 'rgba(255,255,255,0.15)', transition: 'background 0.15s' }} />
          </div>

          {/* Right: form */}
          <div style={{ flex: 1, padding: '1rem 1.25rem', overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              <div>
                <label style={P_LABEL}>Datum</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={P_FIELD} />
              </div>
              <div>
                <label style={P_LABEL}>Valuta</label>
                <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} style={P_FIELD} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={P_LABEL}>Beskrivning</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="t.ex. Cisco Meraki MR33" style={P_FIELD} />
              </div>
              <div>
                <label style={P_LABEL}>Belopp inkl moms</label>
                <input inputMode="decimal" value={amount} onChange={(e) => { setAmount(e.target.value); autoCalcVat(e.target.value, undefined) }} style={P_FIELD} />
              </div>
              <div>
                <label style={P_LABEL}>Momssats %</label>
                <select value={vatRate} onChange={(e) => { setVatRate(e.target.value); autoCalcVat(undefined, e.target.value) }} style={P_FIELD}>
                  <option value="">V\u00e4lj...</option>
                  <option value="25">25%</option>
                  <option value="12">12% (livsmedel)</option>
                  <option value="6">6% (kultur/b\u00f6cker)</option>
                  <option value="0">0% (momsfri)</option>
                </select>
              </div>
              <div>
                <label style={P_LABEL}>Belopp exkl moms</label>
                <input inputMode="decimal" value={amountExVat} onChange={(e) => setAmountExVat(e.target.value)} style={{ ...P_FIELD, color: 'var(--text-muted)' }} />
              </div>
              <div>
                <label style={P_LABEL}>Momsbelopp</label>
                <input inputMode="decimal" value={vatAmount} onChange={(e) => setVatAmount(e.target.value)} style={{ ...P_FIELD, color: 'var(--text-muted)' }} />
              </div>
              <div>
                <label style={P_LABEL}>Konto (BAS)</label>
                <input inputMode="numeric" value={accountCode} onChange={(e) => setAccountCode(e.target.value)} placeholder="5410" style={P_FIELD} />
              </div>
              <div>
                <label style={P_LABEL}>Kontobeskrivning</label>
                <input value={accountLabel} onChange={(e) => setAccountLabel(e.target.value)} placeholder="F\u00f6rbrukningsinventarier" style={P_FIELD} />
              </div>
            </div>

            {msg && <div style={{ marginTop: '0.6rem', padding: '0.4rem 0.7rem', borderRadius: 6, fontSize: '0.85rem', background: msg.startsWith('Fel') ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', color: msg.startsWith('Fel') ? '#ef4444' : '#10b981' }}>{msg}</div>}

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
              <button onClick={() => onSave(false)} disabled={saving} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', flex: 1 }}>
                {saving ? 'Sparar...' : '\u2713 Verifiera & spara'}
              </button>
              <button onClick={() => onSave(true)} disabled={saving} style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid var(--border)', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.9rem', cursor: 'pointer', flex: 1 }}>
                {saving ? '...' : 'Spara & n\u00e4sta \u25b6'}
              </button>
            </div>
            <div style={{ marginTop: '0.6rem', fontSize: '0.7rem', color: 'var(--text-muted)', opacity: 0.6 }}>
              Dra i mittlinjen f\u00f6r att \u00e4ndra storlek \u00b7 Escape st\u00e4nger
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Side-by-side duplikatjämförelse — visar alla kvitton i en grupp
// med filförhandsvisning, fältjämförelse och ta bort-knappar.
// ============================================================

const FIELD_LABELS: { key: keyof CloudReceipt; label: string }[] = [
  { key: 'date', label: 'Datum' },
  { key: 'title', label: 'Beskrivning' },
  { key: 'amount', label: 'Belopp inkl moms' },
  { key: 'amountExVat', label: 'Belopp exkl moms' },
  { key: 'vatAmount', label: 'Momsbelopp' },
  { key: 'vatRate', label: 'Momssats' },
  { key: 'currency', label: 'Valuta' },
  { key: 'accountCode', label: 'Konto' },
  { key: 'accountLabel', label: 'Kontobeskrivning' },
  { key: 'category', label: 'Kategori' },
  { key: 'filename', label: 'Filnamn' },
  { key: 'size', label: 'Storlek' },
  { key: 'fileHash', label: 'SHA-hash' },
]

function DupComparePopup({ group, companyId, onClose, onDeleted }: {
  group: DupScanGroup
  companyId: string
  onClose: () => void
  onDeleted: (id: string) => void
}) {
  const [deleting, setDeleting] = useState<string | null>(null)
  const [xaiVerifying, setXaiVerifying] = useState(false)
  const [xaiResult, setXaiResult] = useState<string | null>(null)
  const [splitPct, setSplitPct] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const dragging = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  function onDragStart(e: React.MouseEvent) {
    e.preventDefault()
    dragging.current = true
    setIsDragging(true)
    const onMove = (ev: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const pct = ((ev.clientX - rect.left) / rect.width) * 100
      setSplitPct(Math.min(80, Math.max(20, pct)))
    }
    const onUp = () => { dragging.current = false; setIsDragging(false); document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  async function doDelete(r: CloudReceipt) {
    setDeleting(r.id)
    try {
      await deleteReceiptCloud(companyId, r)
      onDeleted(r.id)
    } catch (e) {
      alert('Kunde inte ta bort: ' + (e as Error).message)
    } finally {
      setDeleting(null)
    }
  }

  async function runXaiVerify() {
    if (group.receipts.length < 2) return
    setXaiVerifying(true)
    setXaiResult(null)
    try {
      // Run AI analysis on both to compare fields
      const results = await Promise.all(
        group.receipts.slice(0, 2).map((r) => aiAnalyzeReceipt(companyId, r, false))
      )
      const [a, b] = group.receipts
      // Compare extracted data
      const same: string[] = []
      const diff: string[] = []
      for (const f of FIELD_LABELS) {
        const va = a[f.key]
        const vb = b[f.key]
        if (va == null && vb == null) continue
        if (va === vb) same.push(f.label)
        else if (va != null && vb != null) diff.push(f.label + ': ' + String(va) + ' vs ' + String(vb))
      }
      const verdict = group.reason === 'exact-hash'
        ? 'Identisk fil (samma SHA-hash). S\u00e4ker duplikat \u2014 en kan tas bort.'
        : diff.length === 0
          ? 'Alla f\u00e4lt \u00e4r identiska. Trolig duplikat.'
          : 'Skiljer sig p\u00e5: ' + diff.join('; ') + '. Granska manuellt.'
      setXaiResult(
        'xAI-analys klar. ' + verdict +
        (results.some((r) => r.updated) ? ' (AI uppdaterade tomma f\u00e4lt)' : '')
      )
    } catch (e) {
      setXaiResult('AI-verifiering misslyckades: ' + (e as Error).message)
    } finally {
      setXaiVerifying(false)
    }
  }

  const receipts = group.receipts
  const reasonLabel = group.reason === 'exact-hash' ? 'Identiska filer' : group.reason === 'same-filename' ? 'Samma filnamn' : 'Liknande belopp+datum'
  const reasonColor = group.reason === 'exact-hash' ? '#ef4444' : group.reason === 'same-filename' ? '#f59e0b' : '#3b82f6'

  // For 2-receipt groups: side-by-side preview. For 3+: list view with previews.
  const showSideBySide = receipts.length === 2

  function renderPreview(r: CloudReceipt, style?: React.CSSProperties) {
    const isImage = r.mime?.startsWith('image/')
    const isPdf = r.mime === 'application/pdf'
    if (!r.downloadUrl) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', ...style }}>Ingen f\u00f6rhandsgranskning</div>
    if (isPdf) return <iframe src={r.downloadUrl} title={r.filename} style={{ flex: 1, border: 'none', width: '100%', ...style }} />
    if (isImage) return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem', overflow: 'auto', background: '#111827', ...style }}>
        <img src={r.downloadUrl} alt={r.filename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }} />
      </div>
    )
    return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', ...style }}>
      <a href={r.downloadUrl} target="_blank" rel="noopener" style={{ color: 'var(--primary)' }}>{'\u00d6ppna fil \u2197'}</a>
    </div>
  }

  function renderFieldRow(key: keyof CloudReceipt, label: string) {
    const values = receipts.map((r) => r[key])
    const allSame = values.every((v) => v === values[0])
    const displayVal = (v: unknown) => {
      if (v == null) return '\u2014'
      if (key === 'size') return ((v as number) / 1024).toFixed(0) + ' KB'
      if (key === 'vatRate') return ((v as number) * 100).toFixed(0) + '%'
      if (key === 'fileHash') return (v as string).slice(0, 12) + '\u2026'
      if (key === 'amount' || key === 'amountExVat' || key === 'vatAmount') return fmt(v as number)
      return String(v)
    }
    return (
      <tr key={key} style={{ borderTop: '1px solid var(--border)' }}>
        <td style={{ padding: '3px 8px', fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{label}</td>
        {values.map((v, i) => (
          <td key={i} style={{
            padding: '3px 8px', fontSize: '0.8rem',
            color: allSame ? 'var(--text)' : '#f97316',
            fontWeight: allSame ? 400 : 600,
          }}>
            {displayVal(v)}
          </td>
        ))}
      </tr>
    )
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        display: 'flex', flexDirection: 'column', zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, margin: '0.5rem', flex: 1,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.6rem 1rem', borderBottom: '1px solid var(--border)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ fontSize: '1rem', fontWeight: 700 }}>Duplikatj\u00e4mf\u00f6relse</div>
            <span style={{
              background: reasonColor + '22', color: reasonColor,
              padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600,
            }}>
              {reasonLabel}
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {receipts.length + ' kvitton'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={runXaiVerify}
              disabled={xaiVerifying}
              style={{
                background: xaiVerifying ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)',
                border: '1px solid var(--border)', borderRadius: 6,
                padding: '4px 12px', fontSize: '0.8rem',
                color: xaiVerifying ? 'var(--text-muted)' : '#fff',
                cursor: xaiVerifying ? 'wait' : 'pointer',
              }}
            >
              {xaiVerifying ? 'xAI verifierar...' : 'xAI Verifiera duplikat'}
            </button>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: '1.3rem', cursor: 'pointer', padding: '0.25rem',
            }}>{'\u2715'}</button>
          </div>
        </div>

        {/* xAI result bar */}
        {xaiResult && (
          <div style={{
            padding: '0.5rem 1rem', borderBottom: '1px solid var(--border)',
            fontSize: '0.85rem', flexShrink: 0,
            background: xaiResult.includes('misslyckades') ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)',
            color: xaiResult.includes('misslyckades') ? '#ef4444' : '#10b981',
          }}>
            {xaiResult}
          </div>
        )}

        {/* Body */}
        <div ref={containerRef} style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
          {/* Transparent overlay during drag — prevents iframes from stealing mouse events */}
          {isDragging && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'col-resize' }} />
          )}
          {showSideBySide ? (
            <>
              {/* Left receipt preview */}
              <div style={{ width: splitPct + '%', flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{receipts[0].title || receipts[0].filename}</span>
                  <button
                    onClick={() => doDelete(receipts[0])}
                    disabled={deleting === receipts[0].id}
                    style={{
                      background: 'transparent', border: '1px solid var(--danger)',
                      borderRadius: 4, padding: '2px 8px', fontSize: '0.7rem',
                      color: 'var(--danger)', cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    {deleting === receipts[0].id ? 'Tar bort...' : '\ud83d\uddd1 Ta bort denna'}
                  </button>
                </div>
                {renderPreview(receipts[0])}
              </div>

              {/* Draggable divider — wide hit area */}
              <div
                onMouseDown={onDragStart}
                style={{
                  width: 12, cursor: 'col-resize',
                  background: 'transparent',
                  flexShrink: 0, position: 'relative', zIndex: 3,
                }}
              >
                <div style={{
                  position: 'absolute', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)',
                  width: 4, background: isDragging ? 'var(--primary)' : 'var(--border)',
                  transition: 'background 0.15s',
                }} />
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                  width: 6, height: 48, borderRadius: 3,
                  background: isDragging ? 'var(--primary)' : 'rgba(255,255,255,0.15)',
                  transition: 'background 0.15s',
                }} />
              </div>

              {/* Right receipt preview */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{receipts[1].title || receipts[1].filename}</span>
                  <button
                    onClick={() => doDelete(receipts[1])}
                    disabled={deleting === receipts[1].id}
                    style={{
                      background: 'transparent', border: '1px solid var(--danger)',
                      borderRadius: 4, padding: '2px 8px', fontSize: '0.7rem',
                      color: 'var(--danger)', cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    {deleting === receipts[1].id ? 'Tar bort...' : '\ud83d\uddd1 Ta bort denna'}
                  </button>
                </div>
                {renderPreview(receipts[1])}
              </div>
            </>
          ) : (
            /* 3+ receipts: vertical list with inline previews */
            <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.5rem' }}>
                {receipts.map((r) => (
                  <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 6, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {receiptListTitle(r)}
                      </span>
                      <button
                        onClick={() => doDelete(r)}
                        disabled={deleting === r.id}
                        style={{
                          background: 'transparent', border: '1px solid var(--danger)',
                          borderRadius: 4, padding: '2px 6px', fontSize: '0.65rem',
                          color: 'var(--danger)', cursor: 'pointer', flexShrink: 0,
                        }}
                      >
                        {deleting === r.id ? '...' : '\ud83d\uddd1'}
                      </button>
                    </div>
                    {renderPreview(r, { minHeight: 200, maxHeight: 300 })}
                    <div style={{ padding: '0.3rem 0.5rem', fontSize: '0.72rem', color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
                      {r.date || ''}{r.amount != null ? ' \u00b7 ' + fmt(r.amount) + ' ' + (r.currency || '') : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Field comparison table */}
        <div style={{ maxHeight: 220, overflowY: 'auto', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                <th style={{ padding: '4px 8px', textAlign: 'left', fontSize: '0.7rem', color: 'var(--text-muted)' }}>F\u00e4lt</th>
                {receipts.map((r, i) => (
                  <th key={i} style={{ padding: '4px 8px', textAlign: 'left', fontSize: '0.7rem', color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {receiptListTitle(r)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FIELD_LABELS.map((f) => renderFieldRow(f.key, f.label))}
            </tbody>
          </table>
        </div>

        {/* Footer tips */}
        <div style={{ padding: '0.4rem 1rem', borderTop: '1px solid var(--border)', fontSize: '0.7rem', color: 'var(--text-muted)', opacity: 0.6, flexShrink: 0 }}>
          {'Dra i mittlinjen f\u00f6r att \u00e4ndra storlek \u00b7 Orange = olika v\u00e4rden \u00b7 Escape st\u00e4nger'}
        </div>
      </div>
    </div>
  )
}

/** Recursively read files from dropped folder entries */
async function readEntries(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) {
    return new Promise<File[]>((resolve) => {
      (entry as FileSystemFileEntry).file((f) => {
        // Only accept PDFs and images
        if (/\.(pdf|png|jpg|jpeg|gif|webp)$/i.test(f.name) || f.type.startsWith('image/') || f.type === 'application/pdf') {
          resolve([f])
        } else {
          resolve([])
        }
      }, () => resolve([]))
    })
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader()
    const entries = await new Promise<FileSystemEntry[]>((resolve) => {
      const all: FileSystemEntry[] = []
      const readBatch = () => {
        reader.readEntries((batch) => {
          if (batch.length === 0) { resolve(all); return }
          all.push(...batch)
          readBatch()
        }, () => resolve(all))
      }
      readBatch()
    })
    const nested = await Promise.all(entries.map(readEntries))
    return nested.flat()
  }
  return []
}

async function extractFilesFromDrop(e: React.DragEvent): Promise<File[]> {
  const items = e.dataTransfer.items
  if (!items || items.length === 0) return Array.from(e.dataTransfer.files)

  const entries: FileSystemEntry[] = []
  for (let i = 0; i < items.length; i++) {
    const entry = items[i].webkitGetAsEntry?.()
    if (entry) entries.push(entry)
  }

  if (entries.length === 0) return Array.from(e.dataTransfer.files)

  const allFiles = await Promise.all(entries.map(readEntries))
  return allFiles.flat()
}

function DropZone({
  label,
  description,
  accent,
  onFiles,
}: {
  label: string
  description: string
  accent: string
  onFiles: (files: FileList | File[] | null) => void
}) {
  const [over, setOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={async (e) => {
        e.preventDefault()
        setOver(false)
        const files = await extractFilesFromDrop(e)
        if (files.length > 0) onFiles(files)
      }}
      onClick={() => inputRef.current?.click()}
      className="card"
      style={{
        borderLeft: '6px solid ' + accent,
        cursor: 'pointer',
        background: over ? 'rgba(59,130,246,0.08)' : 'var(--bg-card)',
      }}
    >
      <div className="card__label">{label}</div>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
        {description}
      </div>
      <div
        style={{
          marginTop: '1rem',
          padding: '1.25rem',
          border: '2px dashed ' + (over ? accent : 'var(--border)'),
          borderRadius: 8,
          textAlign: 'center',
          fontSize: '0.9rem',
          color: 'var(--text-muted)',
        }}
      >
        {'\ud83d\udcc4 Sl\u00e4pp filer eller mappar h\u00e4r, eller klicka f\u00f6r att v\u00e4lja'}
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/*"
        onChange={(e) => onFiles(e.target.files)}
        style={{ display: 'none' }}
      />
    </div>
  )
}
